import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../components/Modal';
import { contractApi, milestoneApi } from '../services/api';
import type { ContractReplacementStatus } from '../types';

type ResolutionAction = 'remediation' | 'replacement' | 'variation';

const openReplacementStatuses: ContractReplacementStatus[] = [
  'draft', 'signed', 'awaiting_payment', 'refund_pending'
];

/** A warning, not a transition: accepting never closes the other resolution path. */
export function useResolutionOverlapWarning(scopeId: string | undefined) {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<ResolutionAction | null>(null);
  const [variationAlreadyOpen, setVariationAlreadyOpen] = useState(false);
  const activeCheck = useRef<object | null>(null);
  const resolveDecision = useRef<((confirmed: boolean) => void) | null>(null);

  useEffect(() => {
    setPendingAction(null);
    return () => {
      // Navigating away must not let a late read/confirmation perform a mutation.
      activeCheck.current = null;
      resolveDecision.current?.(false);
      resolveDecision.current = null;
    };
  }, [scopeId]);

  const decide = useCallback((confirmed: boolean) => {
    const resolve = resolveDecision.current;
    resolveDecision.current = null;
    setPendingAction(null);
    resolve?.(confirmed);
  }, []);

  const confirmOverlap = useCallback(
    async (
      action: ResolutionAction,
      contractId: string,
      milestoneId?: string
    ): Promise<boolean> => {
      if (activeCheck.current) return false;
      const check = {};
      activeCheck.current = check;
      try {
        // Read at click time: another party/tab may have started the other path.
        // Read failures propagate to the caller's error UI instead of skipping the warning.
        let overlaps: boolean;
        setVariationAlreadyOpen(false);
        if (action === 'remediation') {
          const replacement = await contractApi.getReplacement(contractId);
          overlaps =
            !!replacement &&
            replacement.originalContractId === contractId &&
            (!replacement.amendmentScope ||
              !milestoneId ||
              replacement.amendmentScope.milestones.some(
                (stage) => stage.id === milestoneId
              )) &&
            openReplacementStatuses.includes(replacement.status) &&
            !['withdrawn', 'rejected'].includes(
              replacement.proposalStatus ?? ''
            );
          const stageVariationOpen = overlaps && !!replacement?.amendmentScope;
          if (activeCheck.current === check)
            setVariationAlreadyOpen(stageVariationOpen);
          if (milestoneId && scopeId) {
            const contracts = await contractApi.getContracts(scopeId);
            const candidates = contracts.filter(
              (contract) =>
                contract.contractType === 'variation' &&
                contract.parentContractId === contractId &&
                (contract.versionStatus ?? 'current') === 'current' &&
                contract.draftStatus !== 'draft_rejected' &&
                contract.draftStatus !== 'signed_by_both' &&
                contract.signingStatus !== 'declined'
            );
            const scopes = await Promise.all(
              candidates.map((contract) =>
                contractApi.getConflictVariationScope(contract.id)
              )
            );
            const variationOpen = scopes.some(
              (scope) => scope?.milestoneId === milestoneId
            );
            if (activeCheck.current === check)
              setVariationAlreadyOpen(stageVariationOpen || variationOpen);
            overlaps ||= variationOpen;
          }
        } else {
          const milestones = await milestoneApi.getMilestones(contractId);
          overlaps = milestones.some(
            (milestone) =>
              milestone.contractId === contractId &&
              (!milestoneId || milestone.id === milestoneId) &&
              ['under_resolution', 'ready_for_reattestation'].includes(
                milestone.deliveryStatus ?? ''
              )
          );
        }
        if (activeCheck.current !== check) return false;
        if (!overlaps) return true;
        return await new Promise<boolean>((resolve) => {
          resolveDecision.current = resolve;
          setPendingAction(action);
        });
      } finally {
        if (activeCheck.current === check) activeCheck.current = null;
      }
    },
    [scopeId]
  );

  const confirmationDialog = pendingAction && (
    <Modal
      title={t('resolutionOverlap.title')}
      onClose={() => decide(false)}
      zIndex={1070}
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={() => decide(false)}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn-warning" onClick={() => decide(true)}>
          {t('resolutionOverlap.continueBoth')}
        </button>
      </>}
    >
      <p>{t(pendingAction === 'variation' ? 'conflictVariation.remediationAlreadyOpen' :
        pendingAction === 'remediation' && variationAlreadyOpen ? 'conflictVariation.variationAlreadyOpen' :
        pendingAction === 'remediation' ? 'resolutionOverlap.replacementAlreadyOpen' : 'resolutionOverlap.remediationAlreadyOpen')}</p>
      <p className="mb-0">{t(pendingAction === 'variation' || variationAlreadyOpen ? 'conflictVariation.overlapConsequences' : 'resolutionOverlap.consequences')}</p>
    </Modal>
  );

  return { confirmOverlap, confirmationDialog };
}
